import { FC, ReactNode } from 'react';
import { ExclamationTriangleIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';
import { cn } from '@/utils/classNames';

type ModalVariant = 'success' | 'warning' | 'error';

type AdminConfirmModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  variant?: ModalVariant;
  title: string;
  message: ReactNode;
  warning?: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
};

const variantConfig: Record<ModalVariant, { icon: typeof ExclamationTriangleIcon; iconBg: string; iconColor: string; buttonColor: string }> = {
  success: {
    icon: CheckCircleIcon,
    iconBg: 'bg-green-100',
    iconColor: 'text-green-600',
    buttonColor: 'bg-green-600 hover:bg-green-700',
  },
  warning: {
    icon: ExclamationTriangleIcon,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    buttonColor: 'bg-red-600 hover:bg-red-700',
  },
  error: {
    icon: XCircleIcon,
    iconBg: 'bg-red-100',
    iconColor: 'text-red-600',
    buttonColor: 'bg-red-600 hover:bg-red-700',
  },
};

const AdminConfirmModal: FC<AdminConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  variant = 'warning',
  title,
  message,
  warning,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
}) => {
  const config = variantConfig[variant];
  const Icon = config.icon;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50" onClick={isLoading ? undefined : onClose} />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-xl max-w-md w-full mx-4 p-6">
        {/* Close button */}
        <button
          onClick={onClose}
          disabled={isLoading}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 disabled:opacity-50"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Content */}
        <div className="flex flex-col items-center text-center">
          <div className={cn('w-16 h-16 rounded-full flex items-center justify-center mb-4', config.iconBg)}>
            <Icon className={cn('w-8 h-8', config.iconColor)} />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">{title}</h3>
          <p className="text-gray-600 mb-4">{message}</p>
          {warning && (
            <p className="text-sm text-red-600 bg-red-50 px-4 py-3 rounded-lg w-full">{warning}</p>
          )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 px-4 py-3 text-gray-700 font-semibold rounded-xl hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={cn(
              'flex-1 px-4 py-3 text-white font-semibold rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center',
              config.buttonColor
            )}
          >
            {isLoading ? <span className="loading loading-spinner loading-sm" /> : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminConfirmModal;
